import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import DataDashboard from '../../../common/DataDashboard';
import AdminLayout from '../../layout/AdminLayout';
import UserLayout from '../../../user-v2/layout/UserLayout';
import { AdminUser } from '../../../../types/admin-v2';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import { getDynamicSubItems } from '../../../../utils/getDynamicSubItems';
import './RFQManualProductsDashboard.scss';

interface RFQManualProductsDashboardProps {
  user: AdminUser;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
  manualMode?: 'rfq' | 'global';
}

interface ManualRequestProduct {
  _id: string;
  request_id: string;
  product_id?: string | null;
  product_name: string;
  product_brand?: string;
  category?: string | string[];
  description?: string;
  quantity: number;
  unit?: string;
  base_unit?: string;
  budget?: number;
  currency?: string;
  region?: string;
  is_manual?: boolean;
  notes?: string;
  color?: string;
  dimensions?: string;
  line_number?: number;
  created_at?: string;
  updated_at?: string;
  request_title?: string;
  request_company_request_id?: number;
  request_mail_id?: number;
  request_company_id?: string;
  company_name?: string;
  company_id_display?: string;
}

interface SimilarProduct {
  _id: string;
  product_id: string;
  product_name: string;
  brand?: string;
  category?: string | string[];
  base_unit?: string;
  dimensions?: string;
  color?: string;
  region?: string;
  similarity: number;
}

const RFQManualProductsDashboard: React.FC<RFQManualProductsDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout,
  manualMode = 'rfq'
}) => {
  const location = useLocation();
  const { token } = useAuth();

  const requestIdFromPath = useMemo(() => {
    const pathParts = location.pathname.split('/');
    const openIndex = pathParts.indexOf('open');
    const rfqIndex = pathParts.indexOf('rfq'); // For /dashboard/user/rfq/ pattern
    if (openIndex !== -1 && pathParts.length > openIndex + 1) {
      return pathParts[openIndex + 1];
    } else if (rfqIndex !== -1 && pathParts.length > rfqIndex + 1) {
      return pathParts[rfqIndex + 1];
    }
    return null;
  }, [location.pathname]);

  const isGlobalMode = manualMode === 'global' || !requestIdFromPath;
  const requestId = isGlobalMode ? null : requestIdFromPath;
  
  // Determine base route based on current path
  const isBasicPlanRoute = location.pathname.startsWith('/dashboard/user/rfq/');
  const baseRoute = isBasicPlanRoute ? '/dashboard/user/rfq' : '/dashboard/rfqs/open';

  const manualProductIdFromPath = useMemo(() => {
    const match = location.pathname.match(/manual-products\/([^/]+)/);
    return match ? match[1] : null;
  }, [location.pathname]);

  const baseManualRoute = useMemo(() => {
    if (isGlobalMode) {
      return '/dashboard/products/manual-products';
    }
    return requestId ? `${baseRoute}/${requestId}/manual-products` : null;
  }, [isGlobalMode, requestId, baseRoute]);

  const [manualProducts, setManualProducts] = useState<ManualRequestProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [approveTarget, setApproveTarget] = useState<ManualRequestProduct | null>(null);
  const [editForm, setEditForm] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);

  const [similarProducts, setSimilarProducts] = useState<SimilarProduct[]>([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [similarError, setSimilarError] = useState<string | null>(null);
  const [selectedSimilarId, setSelectedSimilarId] = useState<string | null>(null);
  const defaultSimilarLimit = 200;
  const [similarFetchParams, setSimilarFetchParams] = useState<{ search?: string; limit?: number }>({
    limit: defaultSimilarLimit
  });
  const [assigning, setAssigning] = useState(false);
  const [dashboardKey, setDashboardKey] = useState(0);
  const [creatingProduct, setCreatingProduct] = useState(false);

  const fetchManualProducts = useCallback(async () => {
    if (!token) return;
    if (!isGlobalMode && !requestId) return;

    setIsLoading(true);
    setError(null);

    try {
      const endpoint = isGlobalMode
        ? `${API_ENDPOINTS.REQUEST_V2_MANUAL_PRODUCTS_ALL}?limit=200&sort_order=desc`
        : API_ENDPOINTS.REQUEST_V2_MANUAL_PRODUCTS(requestId!);

      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setManualProducts(data.data);
      } else {
        throw new Error(data.message || 'Failed to load manual products');
      }
    } catch (err: any) {
      console.error('❌ Error fetching manual products:', err);
      setError(err.message || 'Failed to load manual products');
    } finally {
      setIsLoading(false);
    }
  }, [isGlobalMode, requestId, token]);

  useEffect(() => {
    fetchManualProducts();
  }, [fetchManualProducts]);

  const fetchSimilarProducts = useCallback(async (
    targetProductId: string,
    targetRequestId?: string | null,
    options: { search?: string; limit?: number } = {}
  ) => {
    const resolvedRequestId = targetRequestId || requestId;
    if (!resolvedRequestId || !token) return;

    setSimilarLoading(true);
    setSimilarError(null);

    try {
      const baseUrl = API_ENDPOINTS.REQUEST_PRODUCT_SIMILAR(resolvedRequestId, targetProductId);
      const url = new URL(baseUrl);
      const limitParam = options.limit ?? similarFetchParams.limit ?? defaultSimilarLimit;
      if (limitParam) {
        url.searchParams.set('limit', String(limitParam));
      }
      if (options.search && options.search.trim().length > 0) {
        url.searchParams.set('search', options.search.trim());
      }

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setSimilarProducts(data.data);
        if (data.data.length > 0) {
          const firstId = data.data[0]._id || data.data[0].product_id;
          setSelectedSimilarId(firstId ? String(firstId) : null);
        } else {
          setSelectedSimilarId(null);
        }
      } else {
        setSimilarProducts([]);
        setSelectedSimilarId(null);
      }
    } catch (err: any) {
      console.error('❌ Error fetching similar products:', err);
      setSimilarError(err.message || 'Failed to load similar products');
      setSimilarProducts([]);
      setSelectedSimilarId(null);
    } finally {
      setSimilarLoading(false);
    }
  }, [defaultSimilarLimit, requestId, similarFetchParams.limit, token]);

  const manualColumns = useMemo(() => [
    {
      key: 'line_number',
      title: '#',
      type: 'number' as const,
      sortable: true,
      width: '60px',
      editable: false
    },
    {
      key: 'product_name',
      title: 'Product Name',
      type: 'text' as const,
      sortable: true,
      width: '240px',
      editable: false
    },
    {
      key: 'product_brand',
      title: 'Brand',
      type: 'text' as const,
      sortable: true,
      width: '160px',
      editable: false
    },
    {
      key: 'category',
      title: 'Category',
      type: 'text' as const,
      sortable: true,
      width: '180px',
      editable: false,
      render: (value: string | string[]) => {
        if (!value) return '-';
        if (Array.isArray(value)) return value.join(', ');
        return value;
      }
    },
    {
      key: 'quantity',
      title: 'Qty',
      type: 'number' as const,
      sortable: true,
      width: '80px',
      editable: false
    },
    {
      key: 'unit',
      title: 'Unit',
      type: 'text' as const,
      sortable: true,
      width: '80px',
      editable: false
    },
    {
      key: 'budget',
      title: 'Budget',
      type: 'number' as const,
      sortable: true,
      width: '120px',
      editable: false,
      render: (value: number, row: ManualRequestProduct) => {
        if (!value && value !== 0) return '-';
        const currency = row.currency || 'TRY';
        return `${value} ${currency}`;
      }
    },
    {
      key: 'region',
      title: 'Region',
      type: 'text' as const,
      sortable: true,
      width: '90px',
      editable: false,
      render: (value: string) => (value ? value.toUpperCase() : '-')
    },
    {
      key: 'is_manual',
      title: 'Manual',
      type: 'text' as const,
      sortable: true,
      width: '90px',
      editable: false,
      render: (value: boolean) => (value ? 'Yes' : 'No')
    },
    {
      key: 'created_at',
      title: 'Created At',
      type: 'date' as const,
      sortable: true,
      width: '160px',
      editable: false
    }
  ], []);

  const globalColumns = useMemo(() => [
    {
      key: 'request_company_request_id',
      title: 'Company Req #',
      type: 'text' as const,
      sortable: true,
      width: '140px',
      editable: false,
      render: (value: number | undefined) => (value !== undefined ? value : '-')
    },
    {
      key: 'request_title',
      title: 'Request Title',
      type: 'text' as const,
      sortable: true,
      width: '220px',
      editable: false,
      render: (value: string | undefined) => value || '-'
    },
    {
      key: 'company_name',
      title: 'Company',
      type: 'text' as const,
      sortable: true,
      width: '200px',
      editable: false,
      render: (value: string | undefined, row: ManualRequestProduct) =>
        value || row.company_id_display || '-'
    }
  ], []);

  const activeColumns = useMemo(
    () => (isGlobalMode ? [...globalColumns, ...manualColumns] : manualColumns),
    [globalColumns, isGlobalMode, manualColumns]
  );

  const similarDashboardColumns = useMemo(
    () => [
      {
        key: 'product_id',
        title: 'Global ID',
        type: 'text' as const,
        sortable: true,
        width: '120px',
        editable: false
      },
      {
        key: 'name',
        title: 'Product Name',
        type: 'text' as const,
        sortable: true,
        width: '240px',
        editable: false
      },
      {
        key: 'brand',
        title: 'Brand',
        type: 'text' as const,
        sortable: true,
        width: '160px',
        editable: false
      },
      {
        key: 'category',
        title: 'Category',
        type: 'text' as const,
        sortable: true,
        width: '200px',
        editable: false,
        render: (value: string | string[]) => {
          if (!value) return '-';
          if (Array.isArray(value)) return value.join(', ');
          return value;
        }
      },
      {
        key: 'base_unit',
        title: 'Base Unit',
        type: 'text' as const,
        sortable: true,
        width: '110px',
        editable: false
      },
      {
        key: 'region',
        title: 'Region',
        type: 'text' as const,
        sortable: true,
        width: '90px',
        editable: false,
        render: (value: string) => (value ? value.toUpperCase() : '-')
      },
      {
        key: 'similarity',
        title: 'Similarity',
        type: 'number' as const,
        sortable: true,
        width: '120px',
        editable: false,
        render: (value: number) => `${value}%`
      }
    ],
    []
  );

  const similarDashboardData = useMemo(
    () =>
      similarProducts
        .map(product => {
          const rowId = product._id || product.product_id;
          if (!rowId) {
            return null;
          }

          const normalizedCategory = Array.isArray(product.category)
            ? product.category
            : product.category
            ? [product.category]
            : [];

          return {
            _id: String(rowId),
            product_id: product.product_id || '',
            name: product.product_name || '',
            brand: product.brand || '',
            category: normalizedCategory,
            base_unit: product.base_unit || '',
            region: product.region || '',
            similarity: Math.round((product.similarity || 0) * 100)
          };
        })
        .filter((row): row is {
          _id: string;
          product_id: string;
          name: string;
          brand: string;
          category: string[];
          base_unit: string;
          region: string;
          similarity: number;
        } => Boolean(row)),
    [similarProducts]
  );


  const handleSelectionChange = (ids: string[]) => {
    setSelectedIds(ids);
  };

  const resetSelection = () => {
    setSelectedIds([]);
    setDashboardKey(prev => prev + 1);
  };

  const showApprovalPanel = useCallback(
    (product: ManualRequestProduct, options: { navigate?: boolean } = {}) => {
      const { navigate = true } = options;

      setApproveTarget(product);
      setSelectedIds([product._id]);
      setEditForm({
        product_name: product.product_name || '',
        product_brand: product.product_brand || '',
        category: Array.isArray(product.category) ? product.category.join(', ') : (product.category || ''),
        description: product.description || '',
        quantity: product.quantity ?? 0,
        unit: product.unit || '',
        base_unit: product.base_unit || '',
        budget: product.budget ?? '',
        currency: product.currency || 'TRY',
        region: product.region || '',
        notes: product.notes || '',
        color: product.color || '',
        dimensions: product.dimensions || ''
      });
      setSimilarProducts([]);
      setSimilarError(null);
      setSelectedSimilarId(null);
      setSimilarFetchParams({ limit: defaultSimilarLimit });

      const productRequestId =
        typeof product.request_id === 'string'
          ? product.request_id
          : product.request_id
          ? String(product.request_id)
          : null;

      fetchSimilarProducts(product._id, productRequestId, { limit: defaultSimilarLimit });

      if (navigate && baseManualRoute) {
        const targetPath = `${baseManualRoute}/${product._id}`;
        if (location.pathname !== targetPath) {
          onNavigate(targetPath);
        }
      }
    },
    [baseManualRoute, fetchSimilarProducts, location.pathname, onNavigate]
  );

  const openApprovalPanel = (product: ManualRequestProduct) => {
    showApprovalPanel(product);
  };

  useEffect(() => {
    if (!manualProductIdFromPath) {
      return;
    }

    const targetProduct = manualProducts.find(product => product._id === manualProductIdFromPath);
    if (targetProduct && (!approveTarget || approveTarget._id !== targetProduct._id)) {
      showApprovalPanel(targetProduct, { navigate: false });
    }
  }, [approveTarget, manualProductIdFromPath, manualProducts, showApprovalPanel]);

  const handleApproveClick = () => {
    if (selectedIds.length !== 1) return;
    const target = manualProducts.find(product => product._id === selectedIds[0]);
    if (!target) return;
    openApprovalPanel(target);
  };

  const handleRefreshSimilar = () => {
    if (approveTarget) {
      const targetRequestId =
        typeof approveTarget.request_id === 'string'
          ? approveTarget.request_id
          : approveTarget.request_id
          ? String(approveTarget.request_id)
          : null;
      fetchSimilarProducts(approveTarget._id, targetRequestId, similarFetchParams);
    }
  };

  const handleSimilarSelectionChange = useCallback((ids: string[]) => {
    const lastSelected = ids.length > 0 ? ids[ids.length - 1] : null;
    setSelectedSimilarId(lastSelected);
  }, []);

  const handleSimilarDataFetch = useCallback((fetchConfig: { search?: string; limit?: number }) => {
    if (!approveTarget) return;
    const targetRequestId =
      typeof approveTarget.request_id === 'string'
        ? approveTarget.request_id
        : approveTarget.request_id
        ? String(approveTarget.request_id)
        : requestId;

    if (!targetRequestId) return;

    const hasSearch = Object.prototype.hasOwnProperty.call(fetchConfig, 'search');
    const normalizedSearch = fetchConfig?.search?.trim();
    const nextParams: { search?: string; limit?: number } = {
      limit: fetchConfig?.limit ?? similarFetchParams.limit ?? defaultSimilarLimit
    };
    if (hasSearch) {
      if (normalizedSearch) {
        nextParams.search = normalizedSearch;
      }
    } else if (similarFetchParams.search) {
      nextParams.search = similarFetchParams.search;
    }

    setSimilarFetchParams(nextParams);
    fetchSimilarProducts(approveTarget._id, targetRequestId, nextParams);
  }, [approveTarget, defaultSimilarLimit, fetchSimilarProducts, requestId, similarFetchParams]);

  const handleFieldChange = (field: string, value: any) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveManualChanges = async () => {
    if (!approveTarget || !token) return;

    const targetRequestId =
      typeof approveTarget.request_id === 'string'
        ? approveTarget.request_id
        : approveTarget.request_id
        ? String(approveTarget.request_id)
        : requestId;

    if (!targetRequestId) {
      alert('Unable to determine request for this manual product.');
      return;
    }

    const payload: Record<string, any> = {};
    const fieldMappings: Array<[string, string]> = [
      ['product_name', 'product_name'],
      ['product_brand', 'product_brand'],
      ['description', 'description'],
      ['notes', 'notes'],
      ['unit', 'unit'],
      ['base_unit', 'base_unit'],
      ['region', 'region'],
      ['color', 'color'],
      ['dimensions', 'dimensions']
    ];

    fieldMappings.forEach(([formKey, payloadKey]) => {
      if (editForm[formKey] !== (approveTarget as any)[payloadKey]) {
        payload[payloadKey] = editForm[formKey];
      }
    });

    if (Number(editForm.quantity) !== approveTarget.quantity) {
      payload.quantity = Number(editForm.quantity) || 0;
    }

    if (editForm.budget !== approveTarget.budget) {
      const parsedBudget = editForm.budget === '' ? '' : parseFloat(editForm.budget);
      payload.budget = Number.isNaN(parsedBudget) ? 0 : parsedBudget;
    }

    if ((editForm.currency || '').toUpperCase() !== (approveTarget.currency || '').toUpperCase()) {
      payload.currency = (editForm.currency || 'TRY').toUpperCase();
    }

    if (editForm.category !== (Array.isArray(approveTarget.category) ? approveTarget.category.join(', ') : (approveTarget.category || ''))) {
      const categoryValue = editForm.category;
      if (typeof categoryValue === 'string') {
        payload.category = categoryValue
          .split(',')
          .map((item: string) => item.trim())
          .filter(Boolean);
      }
    }

    if (Object.keys(payload).length === 0) {
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${targetRequestId}/products/${approveTarget._id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to update manual product');
      }

      const data = await response.json();
      if (data.success) {
        setManualProducts(prev =>
          prev.map(item => (item._id === approveTarget._id ? { ...item, ...payload } : item))
        );
        setApproveTarget(prev => (prev ? { ...prev, ...payload } : prev));
      } else {
        throw new Error(data.message || 'Failed to update manual product');
      }
    } catch (err: any) {
      console.error('❌ Error updating manual product:', err);
      alert(err.message || 'Failed to update manual product');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAssignSimilar = async () => {
    if (!approveTarget || !token || !selectedSimilarId) return;

    const targetRequestId =
      typeof approveTarget.request_id === 'string'
        ? approveTarget.request_id
        : approveTarget.request_id
        ? String(approveTarget.request_id)
        : requestId;

    if (!targetRequestId) {
      alert('Unable to determine request for this manual product.');
      return;
    }

    setAssigning(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${targetRequestId}/products/${approveTarget._id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_id: selectedSimilarId,
          is_manual: false
        })
      });

      if (!response.ok) {
        throw new Error('Failed to assign manual product to catalog product');
      }

      const data = await response.json();
      if (data.success) {
      alert('Manual product successfully linked to catalog product.');
      fetchManualProducts();
      handleExitApproval('/dashboard/products');
      } else {
        throw new Error(data.message || 'Failed to assign manual product');
      }
    } catch (err: any) {
      console.error('❌ Error assigning manual product:', err);
      alert(err.message || 'Failed to assign manual product');
    } finally {
      setAssigning(false);
    }
  };

  const handleCreateCatalogProduct = async () => {
    if (!approveTarget || !token) {
      return;
    }

    const targetRequestId =
      typeof approveTarget.request_id === 'string'
        ? approveTarget.request_id
        : approveTarget.request_id
        ? String(approveTarget.request_id)
        : requestId;

    if (!targetRequestId) {
      alert('Unable to determine request for this manual product.');
      return;
    }

    const normalizedName = (editForm.product_name || approveTarget.product_name || '').trim();
    if (!normalizedName) {
      alert('Product name is required before creating a catalog product.');
      return;
    }

    const regionCandidate = (editForm.region || approveTarget.region || 'tr').toString().trim().toLowerCase();
    const normalizedRegion = ['tr', 'en', 'de'].includes(regionCandidate) ? regionCandidate : 'tr';

    const categoryString =
      typeof editForm.category === 'string'
        ? editForm.category
        : Array.isArray(approveTarget.category)
        ? approveTarget.category.join(', ')
        : approveTarget.category || '';

    const createPayload = {
      name: normalizedName,
      brand: (editForm.product_brand || approveTarget.product_brand || '').trim(),
      category: categoryString,
      description: (editForm.description || approveTarget.description || '').trim(),
      base_unit: (editForm.base_unit || approveTarget.base_unit || '').trim(),
      dimensions: (editForm.dimensions || approveTarget.dimensions || '').trim(),
      color: (editForm.color || approveTarget.color || '').trim(),
      region: normalizedRegion
    };

    setCreatingProduct(true);
    try {
      const createResponse = await fetch(API_ENDPOINTS.PRODUCTS_V2, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(createPayload)
      });

      if (!createResponse.ok) {
        throw new Error('Failed to create catalog product');
      }

      const createData = await createResponse.json();
      const createdProductObjectId = createData?.data?._id || createData?.id;

      if (!createdProductObjectId) {
        throw new Error('Catalog product created but ID is missing in response');
      }

      const linkResponse = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${targetRequestId}/products/${approveTarget._id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_id: createdProductObjectId,
          is_manual: false,
          region: normalizedRegion
        })
      });

      if (!linkResponse.ok) {
        throw new Error('Failed to link new catalog product to manual product');
      }

      const linkData = await linkResponse.json();
      if (!linkData.success) {
        throw new Error(linkData.message || 'Failed to link new catalog product');
      }

      alert('Manual product promoted to catalog successfully.');
      fetchManualProducts();
      handleExitApproval('/dashboard/products');
    } catch (err: any) {
      console.error('❌ Error creating catalog product from manual entry:', err);
      alert(err.message || 'Failed to create catalog product');
    } finally {
      setCreatingProduct(false);
    }
  };

  const handleExitApproval = (redirectRoute?: string) => {
    setApproveTarget(null);
    setSimilarProducts([]);
    setSimilarError(null);
    setSelectedSimilarId(null);
    setSimilarFetchParams({ limit: defaultSimilarLimit });
    const targetRoute = redirectRoute || baseManualRoute;
    if (targetRoute && location.pathname !== targetRoute) {
      onNavigate(targetRoute);
    }
    resetSelection();
  };

  if (!requestId && !isGlobalMode) {
    return null;
  }

  const headerActions = (
    <div className="manual-products-header-actions">
      {isGlobalMode ? (
        <>
          <button
            className="action-button"
            onClick={() => onNavigate('/dashboard/products')}
          >
            ← Back to Product List
          </button>
          <button
            className="action-button action-button-primary"
            onClick={handleApproveClick}
            disabled={selectedIds.length !== 1}
          >
            Approve
          </button>
        </>
      ) : (
        <>
          <button
            className="action-button"
            onClick={() => onNavigate(`/dashboard/rfqs/open/${requestId}/products`)}
          >
            ← Back to Product Overview
          </button>
          <button
            className="action-button action-button-primary"
            onClick={handleApproveClick}
            disabled={selectedIds.length !== 1}
          >
            Approve
          </button>
        </>
      )}
    </div>
  );

  const approvalHeaderActions = approveTarget ? (
    <div className="manual-products-header-actions">
      <button
        className="action-button"
        onClick={() => handleExitApproval()}
      >
        ← Back to Manual Products
      </button>
      <button
        className="action-button"
        onClick={handleRefreshSimilar}
        disabled={similarLoading}
      >
        Refresh Similar
      </button>
      <button
        className="action-button"
        onClick={() => onNavigate('/dashboard/products')}
      >
        Open Product Catalog
      </button>
      <button
        className="action-button"
        onClick={handleCreateCatalogProduct}
        disabled={creatingProduct || assigning}
      >
        {creatingProduct ? 'Creating...' : 'Add as New Product'}
      </button>
      <button
        className="action-button action-button-primary"
        onClick={handleAssignSimilar}
        disabled={!selectedSimilarId || assigning || creatingProduct}
      >
        {assigning ? 'Assigning...' : 'Approve & Link'}
      </button>
    </div>
  ) : null;

  const approvalHeaderContent = approveTarget ? (
    <div className="manual-approval-panel">
      <div className="panel-header">
        <div>
          <h3>Manual Product Details</h3>
          <p>Review and adjust the manual entry before approving.</p>
        </div>
        <div className="panel-header-actions">
          {isGlobalMode && approveTarget?.request_id && (
            <button
              onClick={() =>
                onNavigate(
                  `/dashboard/rfqs/open/${
                    typeof approveTarget.request_id === 'string'
                      ? approveTarget.request_id
                      : String(approveTarget.request_id)
                  }`
                )
              }
              className="secondary-button"
            >
              View RFQ
            </button>
          )}
        </div>
      </div>

      <div className="panel-body">
        {isGlobalMode && (
          <div className="manual-request-meta">
            <div>
              <span className="meta-label">Request Title:</span>
              <span className="meta-value">{approveTarget.request_title || '-'}</span>
            </div>
            <div>
              <span className="meta-label">Company:</span>
              <span className="meta-value">
                {approveTarget.company_name || approveTarget.company_id_display || '-'}
              </span>
            </div>
            <div>
              <span className="meta-label">Req #:</span>
              <span className="meta-value">
                {approveTarget.request_company_request_id ?? '-'}
              </span>
            </div>
          </div>
        )}

        <div className="manual-product-form">
          <div className="form-grid">
            <label>
              <span>Product Name</span>
              <input
                type="text"
                value={editForm.product_name}
                onChange={(e) => handleFieldChange('product_name', e.target.value)}
              />
            </label>
            <label>
              <span>Brand</span>
              <input
                type="text"
                value={editForm.product_brand}
                onChange={(e) => handleFieldChange('product_brand', e.target.value)}
              />
            </label>
            <label>
              <span>Category</span>
              <input
                type="text"
                value={editForm.category}
                onChange={(e) => handleFieldChange('category', e.target.value)}
                placeholder="Comma separated"
              />
            </label>
            <label>
              <span>Quantity</span>
              <input
                type="number"
                value={editForm.quantity}
                onChange={(e) => handleFieldChange('quantity', e.target.value)}
                min={0}
              />
            </label>
            <label>
              <span>Unit</span>
              <input
                type="text"
                value={editForm.unit}
                onChange={(e) => handleFieldChange('unit', e.target.value)}
              />
            </label>
            <label>
              <span>Base Unit</span>
              <input
                type="text"
                value={editForm.base_unit}
                onChange={(e) => handleFieldChange('base_unit', e.target.value)}
              />
            </label>
            <label>
              <span>Budget</span>
              <input
                type="number"
                value={editForm.budget}
                onChange={(e) => handleFieldChange('budget', e.target.value)}
                step="0.01"
              />
            </label>
            <label>
              <span>Currency</span>
              <input
                type="text"
                value={editForm.currency}
                onChange={(e) => handleFieldChange('currency', e.target.value.toUpperCase())}
                maxLength={3}
              />
            </label>
            <label>
              <span>Region</span>
              <input
                type="text"
                value={editForm.region}
                onChange={(e) => handleFieldChange('region', e.target.value.toLowerCase())}
                placeholder="tr / en / de"
              />
            </label>
            <label>
              <span>Color</span>
              <input
                type="text"
                value={editForm.color}
                onChange={(e) => handleFieldChange('color', e.target.value)}
              />
            </label>
            <label>
              <span>Dimensions</span>
              <input
                type="text"
                value={editForm.dimensions}
                onChange={(e) => handleFieldChange('dimensions', e.target.value)}
              />
            </label>
          </div>

          <label>
            <span>Description</span>
            <textarea
              rows={3}
              value={editForm.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
            />
          </label>

          <label>
            <span>Notes</span>
            <textarea
              rows={2}
              value={editForm.notes}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
            />
          </label>

          <div className="form-actions">
            <button
              className="primary-button"
              onClick={handleSaveManualChanges}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  if (approveTarget) {
    return (
      <DataDashboard
        key={`manual-approval-${approveTarget._id}`}
        title="Similar Catalog Products"
        collection="products"
        data={similarDashboardData}
        columns={similarDashboardColumns}
        isLoading={similarLoading}
        error={similarError}
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        onDataChange={() => {}}
        showAddButton={false}
        showDeleteButton={false}
        showEditButton={false}
        customHeaderActions={approvalHeaderActions}
        onSelectionChange={handleSimilarSelectionChange}
        onDataFetch={handleSimilarDataFetch}
        itemsPerPage={similarFetchParams.limit ?? defaultSimilarLimit}
        paginationInfo={{
          page: 1,
          limit: similarFetchParams.limit ?? defaultSimilarLimit,
          total: similarProducts.length,
          pages: similarProducts.length > 0 ? 1 : 0,
          has_next: false,
          has_prev: false
        }}
        pageDescription="Catalog products filtered by manual entry details"
        layoutType={isBasicPlanRoute ? "user" : "admin"}
        getDynamicSubItems={getDynamicSubItems}
        headerContent={approvalHeaderContent}
        className="rfq-manual-products-dashboard approval-mode"
      />
    );
  }

  return (
    <DataDashboard
      key={dashboardKey}
      title="Manual Products"
      collection="request_products"
      data={manualProducts}
      columns={activeColumns}
      isLoading={isLoading}
      error={error}
      user={user}
      currentRoute={currentRoute}
      onNavigate={onNavigate}
      onLogout={onLogout}
      onDataChange={setManualProducts}
      showAddButton={false}
      showDeleteButton={false}
      showEditButton={false}
      customHeaderActions={headerActions}
      onSelectionChange={handleSelectionChange}
      pageDescription={
        isGlobalMode
          ? 'All manual products across requests awaiting review'
          : 'Manual request products awaiting review and approval'
      }
      layoutType="admin"
      getDynamicSubItems={getDynamicSubItems}
      className="rfq-manual-products-dashboard"
    />
  );
};

export default RFQManualProductsDashboard;

